import { useEffect, useState } from 'react'
import { useViewSort, SORT_OPTIONS, type SortOption } from '../../context/ViewSortContext'
import { cn } from '../../utils'
import { Select } from './Select'
import styles from '../../pages/PageStyles.module.css'

interface ViewSortControlsProps {
  extraOptions?: SortOption[]
  hideViewToggle?: boolean
  /**
   * Controlled overrides. When provided, sort/order are driven by these
   * instead of the global ViewSort context — used by the Releases page so a
   * custom saved-search tab keeps its own sort rather than the global default.
   */
  sort?: string
  order?: 'asc' | 'desc'
  onSortChange?: (sort: string) => void
  onToggleOrder?: () => void
}

export function ViewSortControls({
  extraOptions,
  hideViewToggle,
  sort: sortProp,
  order: orderProp,
  onSortChange,
  onToggleOrder,
}: ViewSortControlsProps) {
  const ctx = useViewSort()
  const { viewMode, setViewMode } = ctx
  const sort = sortProp ?? ctx.sort
  const order = orderProp ?? ctx.order
  const handleSortChange = onSortChange ?? ctx.setSortWithOrder
  const handleToggleOrder = onToggleOrder ?? ctx.toggleOrder
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 768px)').matches)

  const allOptions = extraOptions ? [...SORT_OPTIONS, ...extraOptions] : SORT_OPTIONS
  const activeSortLabel = allOptions.find(option => option.value === sort)?.label ?? 'Sort'

  useEffect(() => {
    const media = window.matchMedia('(max-width: 768px)')
    const update = () => setIsMobile(media.matches)
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [])

  useEffect(() => {
    if (isMobile && viewMode !== 'grid') setViewMode('grid')
  }, [isMobile, setViewMode, viewMode])

  return (
    <>
      <div className={styles.sortControls} data-tour="view-sort">
        <span className={styles.sortLabel}>Sort:</span>
        <span
          className={isMobile ? styles.mobileSortSelect : undefined}
          title={isMobile ? `Sort: ${activeSortLabel}` : undefined}
          data-tour="sort-select"
        >
          {isMobile && <i className="bi bi-sort-down" aria-hidden="true"></i>}
          <Select
            value={sort}
            onChange={(e) => handleSortChange((e.target as HTMLSelectElement).value)}
            options={allOptions.map(opt => ({ value: opt.value, label: opt.label }))}
            className={styles.sortSelect}
            aria-label="Sort releases"
          />
        </span>
        <button
          className={styles.orderToggle}
          onClick={handleToggleOrder}
          title={order === 'desc' ? 'Descending' : 'Ascending'}
        >
          <i className={`bi bi-sort-${order === 'desc' ? 'down' : 'up'}`}></i>
        </button>
      </div>
      {!hideViewToggle && !isMobile && (
        <div className={styles.viewToggle}>
          <button
            className={cn(styles.viewButton, viewMode === 'grid' && styles.active)}
            onClick={() => setViewMode('grid')}
            title="Cover view"
          >
            <i className="bi bi-grid-3x3-gap"></i>
          </button>
          <button
            className={cn(styles.viewButton, viewMode === 'crate' && styles.active)}
            onClick={() => setViewMode('crate')}
            title="Crate view"
          >
            <i className="bi bi-stack"></i>
          </button>
        </div>
      )}
    </>
  )
}
